import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { ImpervaResourcesService } from "./imperva-resources.service";
import {
    ClearImpervaResource,
    LoadAllImpervaResources,
    LoadDataTableImpervaResources,
    SetImpervaResource,
    UpdateCountersImpervaResources,
    LoadAllImpervaCharts,
    LoadImpervaChartsFilteredData
} from "./imperva-resources.actions";
import { finalize, forkJoin, tap } from "rxjs";
import { ImpervaResourcesStateModel, ImpervaResource, ImpervaCounterChartsData } from "./imperva-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { ImpervaResourceTypeEnum } from "src/app/shared/enum/imperva-resource-type.enum";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { DateService } from "src/app/shared/service/date.service";
import { ImpervaResourceTypeLabelPipe } from "src/app/shared/pipe/imperva-resource-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { ImpervaConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";

export const IMPERVA_RESOURCES_TOKEN: StateToken<ImpervaResourcesStateModel> = new StateToken('impervaResources');

const ImpervaResourcesStateModelDefaults: ImpervaResourcesStateModel = {
    resourcesPerAccount: {},
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedResource: {
        account: '',
        age: 0,
        id: '',
        tfstates: [],
        repositories: [],
        issues: [],
        name: '',
        source: '',
        state: 'ignored',
        type: ''
    },
    chartsDataPerAccount: {},
    chartData: {
        filteredComplianceRatioChartData: {
            values: []
        },
        filteredComplianceTotalChartData: {
            values: []
        },
        filteredVersusChartData: {
            values: []
        }
    }
}

@State<ImpervaResourcesStateModel>({
    name: IMPERVA_RESOURCES_TOKEN,
    defaults: ImpervaResourcesStateModelDefaults
})

@Injectable()
export class ImpervaResourcesState {

    private readonly KEY = IMPERVA_RESOURCES_TOKEN.getName()

    constructor(
        private impervaResourcesService: ImpervaResourcesService,
        private store: Store,
        private dateService: DateService,
        private impervaResourceTypeLabelPipe: ImpervaResourceTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getResources(state: ImpervaResourcesStateModel) {
        return (accountId: string) => state.resourcesPerAccount[accountId]
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getCompliantRatio(state: ImpervaResourcesStateModel) {
        return state.compliantRatio
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getTotalItems(state: ImpervaResourcesStateModel) {
        return state.totalItems
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getCompliantItems(state: ImpervaResourcesStateModel) {
        return state.compliantItems
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getNotCompliantItems(state: ImpervaResourcesStateModel) {
        return state.notCompliantItems
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getDatatable(state: ImpervaResourcesStateModel) {
        return state.datatable
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getSelectedResource(state: ImpervaResourcesStateModel) {
        return state.selectedResource
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getFilteredComplianceRatioChartData(state: ImpervaResourcesStateModel) {
        return state.chartData.filteredComplianceRatioChartData
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getFilteredComplianceTotalChartData(state: ImpervaResourcesStateModel) {
        return state.chartData.filteredComplianceTotalChartData
    }

    @Selector([IMPERVA_RESOURCES_TOKEN])
    static getFilteredVersusChartData(state: ImpervaResourcesStateModel) {
        return state.chartData.filteredVersusChartData
    }

    @Action(LoadAllImpervaResources)
    loadAllImpervaResources(ctx: StateContext<ImpervaResourcesStateModel>) {
        const impervaConfig = this.store.selectSnapshot(PreferencesProvidersState.getImpervaConfig);
        const accountIds = impervaConfig.preferences && impervaConfig.preferences.length > 0
            ? impervaConfig.preferences
            : impervaConfig.accounts.map((a: any) => a.account_id);

        if (accountIds.length === 0) return;

        const staleAccountIds = accountIds.filter((id: string) => this.metaService.isStale(this.KEY + "-account-" + id));

        if (staleAccountIds.length === 0) return ctx.dispatch(new LoadDataTableImpervaResources());

        return forkJoin(
            staleAccountIds.map((accountId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-account-" + accountId,
                    this.impervaResourcesService.loadImpervaResources(accountId),
                    resources => {
                        ctx.patchState({
                            resourcesPerAccount: {
                                ...ctx.getState().resourcesPerAccount,
                                [accountId]: resources
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadDataTableImpervaResources()))
        );
    }

    @Action(LoadDataTableImpervaResources)
    loadDataTableImpervaResources(ctx: StateContext<ImpervaResourcesStateModel>) {
        const state = ctx.getState()
        let datatable_resources: ImpervaResource[] = []

        for (const [key, resources] of Object.entries(state.resourcesPerAccount)) {
            resources.forEach((resource) => {
                datatable_resources.push(resource)
            })
        }

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.IMPERVA_RESOURCES]]
        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                datatable_resources = datatable_resources.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable_resources = datatable_resources.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable_resources = datatable_resources.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable_resources = datatable_resources.filter((resource) => filter.value.includes(resource[filter.key as keyof ImpervaResource]))
                } else {
                    datatable_resources = datatable_resources.filter((resource) => resource[filter.key as keyof ImpervaResource] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable_resources
        })
        ctx.dispatch(new UpdateCountersImpervaResources())
    }

    @Action(UpdateCountersImpervaResources)
    updateCountersImpervaResources(ctx: StateContext<ImpervaResourcesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(resource => {
            if (resource.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (resource.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearImpervaResource)
    clearImpervaResource(ctx: StateContext<ImpervaResourcesStateModel>) {
        return ctx.patchState({
            selectedResource: ImpervaResourcesStateModelDefaults.selectedResource
        })
    }

    @Action(SetImpervaResource)
    setImpervaResource(ctx: StateContext<ImpervaResourcesStateModel>, {resource_id, resource_account}: SetImpervaResource) {
        return this.impervaResourcesService.loadImpervaResourceDetails(resource_account, resource_id)
            .pipe(
                tap(value => {
                    return ctx.patchState({
                        selectedResource: value
                    })
                }),
            )
    }

    @Action(LoadAllImpervaCharts)
    loadAllImpervaCharts(ctx: StateContext<ImpervaResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);
        const impervaConfig = this.store.selectSnapshot(PreferencesProvidersState.getImpervaConfig);
        const accountIds = impervaConfig.preferences && impervaConfig.preferences.length > 0
            ? impervaConfig.preferences
            : impervaConfig.accounts.map((a: any) => a.account_id);

        if (accountIds.length === 0) return;

        const staleAccountIds = accountIds.filter((id: string) => this.metaService.isStale(this.KEY + "-charts-account-" + id + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity));

        if (staleAccountIds.length === 0) return ctx.dispatch(new LoadImpervaChartsFilteredData());

        return forkJoin(
            staleAccountIds.map((accountId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-charts-account-" + accountId + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity,
                    this.impervaResourcesService.loadImpervaMeasures(accountId, start_date, end_date, chartsConfig.granularity),
                    charts_payload => {
                        ctx.patchState({
                            chartsDataPerAccount: {
                                ...ctx.getState().chartsDataPerAccount,
                                [accountId]: charts_payload.measures
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadImpervaChartsFilteredData()))
        );
    }

    mergeCountersByDate(input: { [account: string]: ImpervaCounterChartsData }): ImpervaCounterChartsData {
        const result: ImpervaCounterChartsData = {};

        for (const accountId of Object.keys(input)) {
            const dates = input[accountId];

            for (const dateKey of Object.keys(dates)) {
                const types = dates[dateKey];

                if (!result[dateKey]) {
                    result[dateKey] = {};
                }

                for (const typeKey of Object.keys(types)) {
                    if (!result[dateKey][typeKey]) {
                        result[dateKey][typeKey] = {
                            checked: 0,
                            unchecked: 0,
                            ignored: 0
                        };
                    }

                    result[dateKey][typeKey].checked += types[typeKey].checked;
                    result[dateKey][typeKey].unchecked += types[typeKey].unchecked;
                    result[dateKey][typeKey].ignored += types[typeKey].ignored;
                }
            }
        }

        return result;
    }

    @Action(LoadImpervaChartsFilteredData)
    loadImpervaChartsFilteredData(ctx: StateContext<ImpervaResourcesStateModel>) {
        const state = ctx.getState()

        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.IMPERVA_MEASURES]]
        let types: string[] = []
        let account_ids: string[] = []
        filters.forEach(filter => {
            if (filter.key === 'type') {
                types = filter.value
            } else if (filter.key === 'account') {
                account_ids = filter.value
            }
        })
        if (types.length == 0) {
            types = Object.keys(ImpervaResourceTypeEnum)
        }

        let counterChartsData: ImpervaCounterChartsData = {}

        // Filter chartsDataPerAccount by account filter or use all accounts
        let filteredChartsDataPerAccount: Record<string, ImpervaCounterChartsData> = {}
        if (account_ids.length > 0) {
            for (const account_id of account_ids) {
                if (state.chartsDataPerAccount[account_id]) {
                    filteredChartsDataPerAccount[account_id] = state.chartsDataPerAccount[account_id]
                }
            }
        } else {
            filteredChartsDataPerAccount = state.chartsDataPerAccount
        }
        counterChartsData = this.mergeCountersByDate(filteredChartsDataPerAccount)

        for (const [dateString, charts_data] of Object.entries(counterChartsData)) {
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in charts_data) {
                if (types.includes(type)) {
                    const typeMeasures = charts_data[type];
                    compliantCount += typeMeasures.checked;
                    notCompliantCount += typeMeasures.unchecked;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(counterChartsData)[Object.keys(counterChartsData).length - 1]
        let lastMeasure = counterChartsData[lastDate]
        for (const type in lastMeasure) {
            if (types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.impervaResourceTypeLabelPipe.transform(type),
                    "count": typeMeasure.checked + typeMeasure.unchecked + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState({
            chartData: {
                filteredComplianceRatioChartData: {
                    values: complianceRatioChartValues
                },
                filteredComplianceTotalChartData: {
                    values: complianceTotalChartValues
                },
                filteredVersusChartData: {
                    values: complianceVersusChartValues
                }
            }
        })
    }
}
